<template>
  <div class="bf-auth">
    <!-- Painel de marca -->
    <div class="bf-auth__brand">
      <div class="bf-auth__brand-inner">
        <img
          class="bf-auth__brand-logo"
          src="/img/brand/bindfy_logo_new_white.png"
          alt="Bindfy"
        />
        <h2>Integre seus sistemas sem esforço.</h2>
        <p>
          Conecte, transforme, distribua e governe seus dados com facilidade e
          confiança — tudo em um só lugar.
        </p>
      </div>
      <div class="bf-auth__blob bf-auth__blob--1"></div>
      <div class="bf-auth__blob bf-auth__blob--2"></div>
    </div>

    <!-- Formulario -->
    <div class="bf-auth__form-wrap">
      <div class="bf-auth__card">
        <img
          class="bf-auth__logo-mobile"
          src="/img/brand/bindfy_logo_new_purple.png"
          alt="Bindfy"
        />
        <h1 class="bf-auth__title">Bem-vindo de volta</h1>
        <p class="bf-auth__desc">Acesse sua conta para continuar</p>

        <form @submit.prevent="handleSubmit()">
          <div class="bf-field">
            <label>E-mail</label>
            <TextInput
              :label="'Digite o seu e-mail'"
              :type="'email'"
              v-model="signInForm.login"
              :error-messages="
                v$.login.$errors ? v$.login.$errors[0]?.$message : undefined
              "
            />
          </div>

          <div class="bf-field">
            <label>Senha</label>
            <TextInput
              :label="'Digite a sua senha'"
              :type="'password'"
              v-model="signInForm.password"
              :error-messages="
                v$.password.$errors
                  ? v$.password.$errors[0]?.$message
                  : undefined
              "
            />
          </div>

          <div class="bf-auth__row">
            <v-checkbox
              hide-details
              color="primary"
              density="compact"
              v-model="signInForm.remember"
              label="Lembrar deste acesso"
            />
            <router-link
              to="/autenticar/esqueci-minha-senha"
              class="bf-link"
            >
              Esqueceu a senha?
            </router-link>
          </div>

          <PrimaryButton class="mt-0" :is-loading="isLoading" label="Entrar" />

          <!-- MFA Step -->
          <div v-if="mfaRequired" class="bf-field mt-4">
            <label>Código MFA</label>
            <TextInput
              v-model="mfaCode"
              label="Código de 6 dígitos"
              maxlength="6"
              :input-attrs="{ autocomplete: 'one-time-code' }"
            />

            <label class="bf-check">
              <input type="checkbox" v-model="trustDevice" />
              Confiar neste dispositivo
            </label>
            <span v-if="mfaError" class="bf-auth__error">{{ mfaError }}</span>
            <PrimaryButton
              :is-loading="isLoading"
              label="Verificar MFA"
              @click="handleVerifyMfa"
            />
          </div>

          <div class="bf-auth__divider"><span>ou</span></div>

          <div id="google-signin-button" class="bf-auth__google"></div>

          <div class="bf-auth__signup">
            Não tem uma conta?
            <router-link to="/autenticar/criar-conta" class="bf-link">
              Crie uma aqui
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";

// Adiciona a declaração da propriedade 'google' ao tipo Window
declare global {
  interface Window {
    google: any;
  }
}

import { useRouter } from "vue-router";

import { useVuelidate } from "@vuelidate/core";

import { useAuth } from "@/composables/useAuth";

import PrimaryButton from "@/components/Forms/BasicElements/Button/PrimaryButton.vue";

import TextInput from "@/components/Forms/BasicElements/Input/TextInput.vue";

export default defineComponent({
  name: "SignInPage",
  components: {
    TextInput,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();
    const mfaRequired = ref(false);
    const mfaSessionToken = ref("");
    const mfaCode = ref("");
    const mfaError = ref("");
    const trustDevice = ref(false);

    const {
      signInFormRules,
      signInForm,
      autoSignIn,
      signIn,
      isLoading,
      signInWithGoogle,
      verifyMfa,
    } = useAuth();

    const handleGoogleCredentialResponse = (response: any) => {
      const idToken = response.credential;

      signInWithGoogle(idToken)
        .then(() => {
          router.push({ name: "DashboardPage" });
        })
        .catch((error) => {
          console.error("Erro ao fazer login com o Google:", error);
        });
    };

    const handleSubmit = async () => {
      try {
        mfaError.value = "";
        const result = await v$.value.$validate();

        if (!result) return;

        const response = await signIn(signInForm.login, signInForm.password);
        if (response?.data.mfa_required) {
          mfaRequired.value = true;
          mfaSessionToken.value = response.data.mfa_session_token as string;
          return;
        }

        if (response?.data.access_token) {
          router.push({ name: "DashboardPage" });
        }
      } catch (e) {
        console.error(e);
      }
    };

    const handleVerifyMfa = async () => {
      mfaError.value = "";
      try {
        const response = await verifyMfa(
          mfaCode.value,
          mfaSessionToken.value,
          trustDevice.value
        );
        if (response?.data.access_token) {
          router.push({ name: "DashboardPage" });
        }
      } catch (e) {
        mfaError.value = "Erro ao verificar MFA.";
      }
    };

    onMounted(async () => {
      const response = await autoSignIn();
      if (response) {
        router.push({ name: "DashboardPage" });
      }
    });

    onMounted(() => {
      window.google.accounts.id.initialize({
        client_id:
          "631907561214-j7v2u9lavdkv9q59njfr0qc5ih9qb8vs.apps.googleusercontent.com",
        callback: handleGoogleCredentialResponse,
        auto_select: false,
      });

      window.google.accounts.id.renderButton(
        document.getElementById("google-signin-button"),
        {
          theme: "filled_blue", // ou "outline"
          size: "large", // "small", "medium", "large"
          text: "continue_with", // ou "signin_with", "signup_with"
          shape: "square", // "rectangular", "pill", "circle", "square"
          logo_alignment: "center", // "left", "center"
          width: 300, // largura em px
          locale: "pt-BR",
        }
      );

      // Se quiser o prompt automático, pode chamar:
      window.google.accounts.id.prompt();
    });

    const v$ = useVuelidate(signInFormRules, signInForm);

    return {
      signInForm,
      isLoading,
      handleSubmit,
      handleVerifyMfa,
      mfaRequired,
      mfaCode,
      mfaError,
      trustDevice,
      v$,
    };
  },
});
</script>

<style lang="scss" scoped>
.bf-auth {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  background: var(--bg);
}

/* Painel de marca */
.bf-auth__brand {
  position: relative;
  overflow: hidden;
  background: linear-gradient(150deg, var(--brand-deep), var(--brand2));
  color: #fff;
  display: flex;
  align-items: center;
  padding: 56px;
}
.bf-auth__brand-inner {
  position: relative;
  z-index: 2;
  max-width: 560px;
  width: 100%;

  h2 {
    color: #fff;
    font-size: 32px;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.02em;
    margin: 40px 0 16px;
  }
  p {
    font-size: 16px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
  }
}
.bf-auth__brand-logo {
  width: 100%;
  max-width: 480px;
  height: auto;
  display: block;
  margin-bottom: 8px;
}
.bf-auth__blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(10px);
  opacity: 0.35;
  &--1 {
    width: 320px;
    height: 320px;
    background: #fff;
    opacity: 0.12;
    top: -80px;
    right: -60px;
  }
  &--2 {
    width: 260px;
    height: 260px;
    background: var(--brand2);
    bottom: -80px;
    left: -40px;
    opacity: 0.5;
  }
}

/* Formulario */
.bf-auth__form-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
}
.bf-auth__card {
  width: 100%;
  max-width: 400px;
}
.bf-auth__logo-mobile {
  display: none;
  height: 48px;
  margin: 0 auto 24px;
}
.bf-auth__title {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--text);
  margin: 0;
}
.bf-auth__desc {
  font-size: 13.5px;
  color: var(--text2);
  margin: 6px 0 26px;
}

.bf-field {
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: var(--text2);
    margin-bottom: 7px;
  }
}

.bf-auth__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2px 0 18px;
}

.bf-link {
  color: var(--brand);
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  &:hover {
    color: var(--brand-deep);
    text-decoration: underline;
  }
}

.bf-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text2);
  margin: 10px 0;
  cursor: pointer;
}

.bf-auth__error {
  display: block;
  color: var(--err-fg);
  font-size: 12.5px;
  margin: 4px 0 10px;
}

.bf-auth__divider {
  position: relative;
  text-align: center;
  margin: 24px 0;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    background: var(--border);
  }
  span {
    position: relative;
    background: var(--bg);
    padding: 0 14px;
    font-size: 12.5px;
    color: var(--text3);
  }
}

.bf-auth__form-wrap :deep(.bf-auth__divider span) {
  background: var(--bg);
}

.bf-auth__google {
  display: flex;
  justify-content: center;
}

.bf-auth__signup {
  text-align: center;
  font-size: 13.5px;
  color: var(--text2);
  margin-top: 22px;
}

/* Responsivo */
@media (max-width: 900px) {
  .bf-auth {
    grid-template-columns: 1fr;
  }
  .bf-auth__brand {
    display: none;
  }
  .bf-auth__logo-mobile {
    display: block;
  }
}
</style>
